import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import DataTable from '../common/DataTable';
import { formatCurrency, today, firstOfMonth } from '../../utils/helpers';

// Stock is valued at the item's cost (purchase rate) so that, for both Qty and
// Value, Opening + Inward - Outward = Closing reconciles exactly.
export default function StockLedgerSummary() {
  const { state } = useApp();
  const [scope, setScope] = useState('all');          // 'all' | 'item'
  const [selectedItem, setSelectedItem] = useState('');
  const [filterType, setFilterType] = useState('both'); // 'qty' | 'values' | 'both'
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());

  const rows = useMemo(() => {
    // Collect every stock movement keyed by item id (mirrors Stock Ledger logic)
    const movesByItem = {};
    const push = (itemId, date, inQty, outQty) => {
      if (!itemId) return;
      (movesByItem[itemId] = movesByItem[itemId] || []).push({ date, in: inQty, out: outQty });
    };
    state.salesInvoices.forEach(d => d.items?.forEach(li => push(li.itemId, d.date, 0, Number(li.qty) || 0)));
    state.purchaseBills.forEach(d => d.items?.forEach(li => push(li.itemId, d.date, Number(li.qty) || 0, 0)));
    state.salesReturns.forEach(d => d.items?.forEach(li => push(li.itemId, d.date, Number(li.qty) || 0, 0)));
    state.purchaseReturns.forEach(d => d.items?.forEach(li => push(li.itemId, d.date, 0, Number(li.qty) || 0)));
    state.deliveryChallans.forEach(d => d.items?.forEach(li => push(li.itemId, d.date, 0, Number(li.qty) || 0)));
    // Production: inputs consumed (out), outputs produced (in)
    state.productions?.forEach(d => {
      d.inputs?.forEach(li => push(li.itemId, d.date, 0, Number(li.qty) || 0));
      d.outputs?.forEach(li => push(li.itemId, d.date, Number(li.qty) || 0, 0));
    });
    // Stock Issue to Consumption (out)
    state.stockIssues?.forEach(d => d.items?.forEach(li => push(li.itemId, d.date, 0, Number(li.qty) || 0)));

    const itemsToShow = scope === 'item' && selectedItem
      ? state.items.filter(i => i.id === selectedItem)
      : state.items;

    return itemsToShow.map(item => {
      const moves = movesByItem[item.id] || [];
      let openingQty = Number(item.openingStock) || 0;
      let inwardQty = 0;
      let outwardQty = 0;
      moves.forEach(m => {
        if (startDate && (m.date || '') < startDate) { openingQty += m.in - m.out; return; }
        if (endDate && (m.date || '') > endDate) return; // outside period (future)
        inwardQty += m.in;
        outwardQty += m.out;
      });
      const closingQty = openingQty + inwardQty - outwardQty;
      const rate = Number(item.purchaseRate) || 0;
      return {
        id: item.id,
        name: item.name,
        unit: item.unit,
        openingQty, inwardQty, outwardQty, closingQty,
        openingVal: openingQty * rate,
        inwardVal: inwardQty * rate,
        outwardVal: outwardQty * rate,
        closingVal: closingQty * rate,
      };
    });
  }, [scope, selectedItem, startDate, endDate, state]);

  const showQty = filterType === 'qty' || filterType === 'both';
  const showVal = filterType === 'values' || filterType === 'both';

  const columns = useMemo(() => {
    const money = v => formatCurrency(v);
    const qty = (key, label) => ({ key, label, align: 'right', sortable: true });
    const val = (key, label) => ({ key, label, align: 'right', sortable: true, render: money, exportValue: r => r[key] });
    const cols = [{ key: 'name', label: 'Item Name', sortable: true }];
    const group = (q, v, label) => {
      if (showQty) cols.push(qty(q, showVal ? `${label} Qty` : label));
      if (showVal) cols.push(val(v, showVal && showQty ? `${label} Value` : `${label} Value`));
    };
    group('openingQty', 'openingVal', 'Opening');
    group('inwardQty', 'inwardVal', 'Inward');
    group('outwardQty', 'outwardVal', 'Outward');
    group('closingQty', 'closingVal', 'Closing');
    return cols;
  }, [showQty, showVal]);

  const grandClosingVal = useMemo(
    () => rows.reduce((s, r) => s + r.closingVal, 0),
    [rows]
  );

  // Totals row (sums every numeric column over the currently shown/filtered rows)
  const totalsRow = (filteredRows) => {
    const sum = key => filteredRows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    const t = {};
    if (showQty) {
      t.openingQty = sum('openingQty');
      t.inwardQty = sum('inwardQty');
      t.outwardQty = sum('outwardQty');
      t.closingQty = sum('closingQty');
    }
    if (showVal) {
      t.openingVal = formatCurrency(sum('openingVal'));
      t.inwardVal = formatCurrency(sum('inwardVal'));
      t.outwardVal = formatCurrency(sum('outwardVal'));
      t.closingVal = formatCurrency(sum('closingVal'));
    }
    return t;
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Stock Ledger Summary</h1>
        {showVal && (
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Closing Value</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(grandClosingVal)}</p>
          </div>
        )}
      </div>

      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[160px]">
            <label className="label">Report Scope</label>
            <select className="input" value={scope} onChange={e => setScope(e.target.value)}>
              <option value="all">All Items</option>
              <option value="item">Item Wise</option>
            </select>
          </div>
          {scope === 'item' && (
            <div className="min-w-[220px]">
              <label className="label">Select Item</label>
              <select className="input" value={selectedItem} onChange={e => setSelectedItem(e.target.value)}>
                <option value="">-- Select Item --</option>
                {state.items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
          )}
          <div className="min-w-[150px]">
            <label className="label">Filter Type</label>
            <select className="input" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="qty">Qty</option>
              <option value="values">Values</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div>
            <label className="label">From Date</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">To Date</label>
            <input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={rows}
          searchFields={['name']}
          exportFilename="stock-ledger-summary"
          totals={totalsRow}
        />
      </div>
    </div>
  );
}
